/**
 * Period Configuration Map
 * Immutable configuration for report period types
 *
 * Provides centralized configuration for:
 * - Display names and labels
 * - Date formatting patterns
 * - Aggregation keys
 * - X-axis labels for charts
 *
 * @module lib/reports/period-config
 */

import { ReportPeriod } from '@/types/reports';

/**
 * Period configuration structure
 */
export interface PeriodConfig {
  /** Full display name (e.g., "Diária", "Mensal") */
  displayName: string;

  /** Short name for compact display (e.g., "Dia", "Mês") */
  shortName: string;

  /** date-fns format string for date formatting */
  dateFormat: string;

  /** Aggregation key used by backend */
  aggregationKey: string;

  /** Label for chart X-axis */
  xAxisLabel: string;
}

/**
 * Immutable period configurations
 * Maps each ReportPeriod to its display and formatting settings
 */
export const PERIOD_CONFIGS: Record<ReportPeriod, PeriodConfig> = {
  [ReportPeriod.DAILY]: {
    displayName: 'Diária',
    shortName: 'Dia',
    dateFormat: 'dd/MM/yyyy',
    aggregationKey: 'day',
    xAxisLabel: 'Dia',
  },
  [ReportPeriod.WEEKLY]: {
    displayName: 'Semanal',
    shortName: 'Semana',
    dateFormat: "'Sem' dd/MM",
    aggregationKey: 'week',
    xAxisLabel: 'Semana',
  },
  [ReportPeriod.MONTHLY]: {
    displayName: 'Mensal',
    shortName: 'Mês',
    dateFormat: 'MMM yyyy',
    aggregationKey: 'month',
    xAxisLabel: 'Mês',
  },
  [ReportPeriod.QUARTERLY]: {
    displayName: 'Trimestral',
    shortName: 'Trimestre',
    dateFormat: "'Q'Q yyyy",
    aggregationKey: 'quarter',
    xAxisLabel: 'Trimestre',
  },
  [ReportPeriod.YEARLY]: {
    displayName: 'Anual',
    shortName: 'Ano',
    dateFormat: 'yyyy',
    aggregationKey: 'year',
    xAxisLabel: 'Ano',
  },
} as const;
